import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from "@angular/common/http";
import { Observable, catchError, from, map, switchMap, throwError } from "rxjs";
import { environment } from "../../environments/environment";
import {
  ComParcelWaybillParcelsCount,
  ParcelFilterOptions,
  ParcelSearchFilters,
  ParcelSearchResponse,
  WaybillParcelsCount,
} from "../interfaces/overview-parcels.interfaces";
import { ExportedFile } from "./overview-harmonizer.service";

// Elenco Colli (pagina /overview)
@Injectable({
  providedIn: "root",
})
export class OverviewParcelsService {
  private apiUrl = `${environment.apiUrl}/tracking`;

  constructor(private http: HttpClient) {}

  /** Una pagina di colli che corrispondono ai filtri, con il totale */
  searchParcels<T>(
    filters: ParcelSearchFilters,
  ): Observable<ParcelSearchResponse<T>> {
    return this.http.post<ParcelSearchResponse<T>>(
      `${this.apiUrl}/parcels`,
      filters,
    );
  }

  /** Opzioni delle tendine con il numero di colli su tutti i risultati: va chiamato quando cambiano i filtri */
  getFilterOptions(
    filters: ParcelSearchFilters,
  ): Observable<ParcelFilterOptions> {
    return this.http.post<ParcelFilterOptions>(
      `${this.apiUrl}/parcels/filter-options`,
      filters,
    );
  }

  /** Colli della bolla di un collo modificati da note e giustifica "per bolla" */
  getWaybillParcelsCount(parcelCode: string): Observable<WaybillParcelsCount> {
    return this.http.get<WaybillParcelsCount>(
      `${this.apiUrl}/parcels/${encodeURIComponent(parcelCode)}/waybill-parcels-count`,
    );
  }

  /** Colli aggiornati dalla modifica "per bolla" di un evento (stessa bolla e sequenza) */
  getComParcelWaybillParcelsCount(
    parcelComplementaryId: number,
  ): Observable<ComParcelWaybillParcelsCount> {
    return this.http.get<ComParcelWaybillParcelsCount>(
      `${this.apiUrl}/comParcels/${parcelComplementaryId}/waybill-parcels-count`,
    );
  }

  /**
   * Tappe di più colli in una chiamata (massimo 100 codici), da codice collo alle sue tappe.
   * I colli inesistenti o non visibili all'utente non compaiono nella risposta.
   */
  searchComParcels<T>(parcelCodes: string[]): Observable<Record<string, T[]>> {
    return this.http.post<Record<string, T[]>>(
      `${this.apiUrl}/comParcels/search`,
      { parcelCodes },
    );
  }

  /**
   * Export Excel di tutti i colli che corrispondono ai filtri (nessuna paginazione).
   * Con withOriginalData il file include anche i dati di consegna originali.
   */
  exportParcelsExcel(
    filters: ParcelSearchFilters,
    withOriginalData: boolean,
  ): Observable<ExportedFile> {
    const params = new HttpParams().set("withOriginalData", withOriginalData);
    return this.http
      .post(`${this.apiUrl}/parcels/export`, filters, {
        params,
        observe: "response",
        responseType: "blob",
      })
      .pipe(
        map((res: HttpResponse<Blob>) => ({
          blob: res.body as Blob,
          fileName: this.extractFileName(res),
        })),
        catchError((err) => this.blobErrorToText(err)),
      );
  }

  // Con responseType "blob" anche il corpo dell'errore è un Blob: lo converte in testo
  // così ErrorHelperService può estrarre il messaggio del backend
  private blobErrorToText(err: HttpErrorResponse): Observable<never> {
    if (!(err.error instanceof Blob)) {
      return throwError(() => err);
    }
    return from(err.error.text()).pipe(
      switchMap((text) =>
        throwError(
          () =>
            new HttpErrorResponse({
              error: text,
              headers: err.headers,
              status: err.status,
              statusText: err.statusText,
              url: err.url ?? undefined,
            }),
        ),
      ),
    );
  }

  private extractFileName(res: HttpResponse<Blob>): string | null {
    const contentDisposition = res.headers.get("Content-Disposition");
    const match = contentDisposition?.match(
      /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i,
    );
    return match ? decodeURIComponent(match[1]) : null;
  }
}
